import React, { useCallback, useEffect, useState } from 'react';

import { Button, Modal, Stack, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';

import CreateAppointmentDrawerForm from './components/CreateAppointmentDrawerForm';
import CreateClassFormDrawer from './components/CreateClassFormDrawer';
import EditAppointmentDrawer from './components/EditAppointmentDrawer';
import EditClassDrawer from './components/EditClassDrawer';
import EventCalendarAgendaView from './components/EventCalendarAgendaView/EventCalendarAgendaView';
import EventCalendarDaySchedulerView from './components/EventCalendarDaySchedulerView';
import EventCalendarMonthView from './components/EventCalendarMonthView';
import type { LoadStatus } from './components/EventCalendarNavigationButtons';
import EventCalendarNavigationButtons from './components/EventCalendarNavigationButtons';
import EventCalendarWeekView from './components/EventCalendarWeekView';
import { EventCalendarView } from './components/types';
import { DragPrefill } from './hooks/useEventDragUpdate';

interface FilterTerms {
  eventTypes: string[];
  coaches: string[];
  locations: string[];
}

interface EventCalendarProps {
  searchFilters: FilterTerms;
  isCreateClassFormOpen: boolean;
  isCreateAppointmentFormOpen: boolean;
  createClassFormHandlers: { open: () => void; close: () => void };
  createAppointmentFormHandlers: { open: () => void; close: () => void };
  defaultView?: EventCalendarView;
  permission?: boolean;
  isDesktop: boolean;
}

export interface SelectedCoach {
  staffId: string;
  name: string;
}

const EventCalendar = ({
  searchFilters,
  isCreateClassFormOpen,
  isCreateAppointmentFormOpen,
  createClassFormHandlers,
  createAppointmentFormHandlers,
  isDesktop,
  permission = true,
}: EventCalendarProps): JSX.Element => {
  const nowDate = new Date();
  const [searchParams] = useSearchParams();

  const [view, setView] = useState<EventCalendarView>(isDesktop ? 'week' : 'day');
  const calendarMaxDate = dayjs(new Date()).add(1, 'year').toDate();
  const [kiloClassId, setKiloClassId] = useState<string | undefined>();
  const [appointmentId, setAppointmentId] = useState<string | undefined>();
  const [classPrefill, setClassPrefill] = useState<DragPrefill | undefined>();
  const [appointmentPrefill, setAppointmentPrefill] = useState<DragPrefill | undefined>();

  const [createEventDate, setCreateEventDate] = useState<Date>(nowDate);
  const [selectedCoach, setSelectedCoach] = useState<SelectedCoach | undefined>();

  const [isSelectEventModalOpen, { open: openSelectEventModal, close: closeSelectEventModal }] =
    useDisclosure(false);
  const [isEditClassFormOpened, { open: openEditClassForm, close: closeEditClassForm }] =
    useDisclosure(false);

  const [isEditAppointmentFormOpened, { open: openEditAppointmentForm, close: closeEditAppointmentForm }] =
    useDisclosure(false);

  const handleCloseClassFormHandler = (): void => {
    createClassFormHandlers.close();
    setSelectedCoach(undefined);
  };

  const handleCloseAppointmentFormHandler = (): void => {
    createAppointmentFormHandlers.close();
    setSelectedCoach(undefined);
  };

  const handleEditClassEvent = useCallback(
    (kcId: string, prefill?: DragPrefill): void => {
      setKiloClassId(kcId);
      setClassPrefill(prefill);
      openEditClassForm();
    },
    [openEditClassForm],
  );

  const handleEditAppointmentEvent = useCallback(
    (aId: string, prefill?: DragPrefill): void => {
      setAppointmentId(aId);
      setAppointmentPrefill(prefill);
      openEditAppointmentForm();
    },
    [openEditAppointmentForm],
  );

  const handleCloseEditClassForm = (): void => {
    closeEditClassForm();
    setKiloClassId(undefined);
    setClassPrefill(undefined);
  };

  const handleCloseEditAppointmentForm = (): void => {
    closeEditAppointmentForm();
    setAppointmentId(undefined);
    setAppointmentPrefill(undefined);
  };

  const handleOpenSelectEventModal = (): void => {
    openSelectEventModal();
  };

  const handleEventCreateSelection = (type: 'class' | 'appointment'): void => {
    closeSelectEventModal();
    if (type === 'class') {
      createClassFormHandlers.open();
    }
    if (type === 'appointment') {
      createAppointmentFormHandlers.open();
    }
  };

  useEffect(() => {
    const eventType = searchParams.get('eventType');
    const eventId = searchParams.get('eventId');

    if (!eventId) return;

    if (eventType === 'class') {
      handleEditClassEvent(eventId);
    } else if (eventType === 'appointment') {
      handleEditAppointmentEvent(eventId);
    }
  }, [handleEditAppointmentEvent, handleEditClassEvent, searchParams]);

  const renderEventCalendarNavigationButtons = useCallback(
    (eventsStatus?: LoadStatus, coachAvailabilityStatus?: LoadStatus): JSX.Element => {
      return (
        <EventCalendarNavigationButtons
          view={view}
          setView={setView}
          isDesktop={isDesktop}
          eventsStatus={eventsStatus}
          selectedDate={createEventDate}
          coachAvailabilityStatus={coachAvailabilityStatus}
          onDateSelect={setCreateEventDate}
        />
      );
    },
    [view, isDesktop, createEventDate, setCreateEventDate],
  );

  return (
    <>
      {view === 'day' && isDesktop && (
        <EventCalendarDaySchedulerView
          eventCalendarNavigationButtons={renderEventCalendarNavigationButtons}
          createEventDate={createEventDate}
          setCreateEventDate={setCreateEventDate}
          searchFilters={searchFilters}
          calendarMaxDate={calendarMaxDate}
          handleEditClassEvent={handleEditClassEvent}
          handleEditAppointmentEvent={handleEditAppointmentEvent}
          handleOpenSelectEventModal={handleOpenSelectEventModal}
          permission={permission}
          setSelectedCoach={setSelectedCoach}
        />
      )}
      {view === 'day' && !isDesktop && (
        <EventCalendarAgendaView
          eventCalendarNavigationButtons={renderEventCalendarNavigationButtons}
          createEventDate={createEventDate}
          setCreateEventDate={setCreateEventDate}
          searchFilters={searchFilters}
          calendarMaxDate={calendarMaxDate}
          handleEditClassEvent={handleEditClassEvent}
          handleEditAppointmentEvent={handleEditAppointmentEvent}
          isDesktop={isDesktop}
        />
      )}
      {view === 'week' && (
        <EventCalendarWeekView
          setCreateEventDate={setCreateEventDate}
          eventCalendarNavigationButtons={renderEventCalendarNavigationButtons}
          handleOpenSelectEventModal={handleOpenSelectEventModal}
          searchFilters={searchFilters}
          calendarMaxDate={calendarMaxDate}
          handleEditClassEvent={handleEditClassEvent}
          handleEditAppointmentEvent={handleEditAppointmentEvent}
          permission={permission}
          isDesktop={isDesktop}
        />
      )}
      {view === 'month' && (
        <EventCalendarMonthView
          setView={setView}
          setCreateEventDate={setCreateEventDate}
          eventCalendarNavigationButtons={renderEventCalendarNavigationButtons}
          searchFilters={searchFilters}
          calendarMaxDate={calendarMaxDate}
          isDesktop={isDesktop}
        />
      )}
      <Modal
        centered
        opened={isSelectEventModalOpen}
        onClose={closeSelectEventModal}
        title={<Title order={4}>Select Event Type</Title>}
        size='sm'
      >
        <Stack px='lg'>
          <Button onClick={() => handleEventCreateSelection('class')}>Class</Button>
          <Button onClick={() => handleEventCreateSelection('appointment')}>Appointment</Button>
        </Stack>
      </Modal>
      <CreateClassFormDrawer
        date={createEventDate}
        opened={isCreateClassFormOpen}
        closeHandler={handleCloseClassFormHandler}
        selectedCoach={selectedCoach}
      />
      <CreateAppointmentDrawerForm
        date={createEventDate}
        opened={isCreateAppointmentFormOpen}
        closeHandler={handleCloseAppointmentFormHandler}
        selectedCoach={selectedCoach}
      />
      <EditClassDrawer
        key={kiloClassId}
        setKiloClassId={setKiloClassId}
        opened={isEditClassFormOpened}
        closeHandler={handleCloseEditClassForm}
        kiloClassId={kiloClassId}
        permission={permission}
        dragPrefill={classPrefill}
      />
      <EditAppointmentDrawer
        key={appointmentId}
        setAppointmentId={setAppointmentId}
        opened={isEditAppointmentFormOpened}
        closeHandler={handleCloseEditAppointmentForm}
        appointmentId={appointmentId}
        permission={permission}
        dragPrefill={appointmentPrefill}
      />
    </>
  );
};

export default EventCalendar;
